import { EventModel, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { pprops } from '@frontend/sports/common/core/utils/redux';
import { createAction, props } from '@ngrx/store';

import BetslipActions from '../../base/store/actions';
import { BetslipPick } from '../../core/picks/betslip-pick';
import { BetslipV1Pick } from '../../core/picks/betslip-v1-pick';
import { BetslipV2OptionMarketPick } from '../../core/picks/betslip-v2-option-market-pick';
import { V1PickId, V2OptionMarketPickId } from '../../core/picks/pick-id';
import { ILineSwitcherErrorState, ILineSwitcherPossibleWinningsState, ILineSwitcherSwapButtonState } from './state';

export interface ILineSwitcherError {
    error: Error;
    keepOpen?: boolean;
    showError?: boolean;
    hasMarkets?: boolean;
}

export class LineSwitcherActions {
    static readonly ACTION_SCHEMA = `${BetslipActions.ACTION_SCHEMA}/line-switcher`;

    private static readonly EFFECT_INITIALIZED = `${LineSwitcherActions.ACTION_SCHEMA}/EFFECT_INITIALIZED`;
    private static readonly INIT_LINE_SWITCHER = `${LineSwitcherActions.ACTION_SCHEMA}/INIT_LINE_SWITCHER`;
    private static readonly OPEN_LINE_SWITCHER = `${LineSwitcherActions.ACTION_SCHEMA}/OPEN_LINE_SWITCHER`;
    private static readonly UPDATE_OPTION_GROUP = `${LineSwitcherActions.ACTION_SCHEMA}/UPDATE_OPTION_GROUP`;
    private static readonly ADD_SWAP_PICK_SELECTION = `${LineSwitcherActions.ACTION_SCHEMA}/ADD_SWAP_PICK_SELECTION`;
    private static readonly UPDATE_POSSIBLE_WINNINGS_STATE = `${LineSwitcherActions.ACTION_SCHEMA}/UPDATE_POSSIBLE_WINNINGS_STATE`;
    private static readonly UPDATE_SWAP_BUTTON_STATE = `${LineSwitcherActions.ACTION_SCHEMA}/UPDATE_SWAP_BUTTON_STATE`;
    private static readonly HANDLE_PUSH_UPDATE = `${LineSwitcherActions.ACTION_SCHEMA}/HANDLE_PUSH_UPDATE`;
    private static readonly HANDLE_SWAP_ERROR = `${LineSwitcherActions.ACTION_SCHEMA}/HANDLE_SWAP_ERROR`;
    private static readonly SWAP_PICK = `${LineSwitcherActions.ACTION_SCHEMA}/SWAP_PICK`;
    private static readonly REMOVE_PICK_HIGHLIGHT = `${LineSwitcherActions.ACTION_SCHEMA}/REMOVE_PICK_HIGHLIGHT`;
    private static readonly CLOSE_LINE_SWITCHER = `${LineSwitcherActions.ACTION_SCHEMA}/CLOSE_LINE_SWITCHER`;
    private static readonly CANCEL_LINE_SWITCHER = `${LineSwitcherActions.ACTION_SCHEMA}/CANCEL_LINE_SWITCHER`;
    private static readonly INIT_HELP_MESSAGE_BOX = `${LineSwitcherActions.ACTION_SCHEMA}/INIT_HELP_MESSAGE_BOX`;
    private static readonly CLOSE_HELP_MESSAGE_BOX = `${LineSwitcherActions.ACTION_SCHEMA}/CLOSE_HELP_MESSAGE_BOX`;
    private static readonly TRACK_SWAP = `${LineSwitcherActions.ACTION_SCHEMA}/TRACK_SWAP`;

    /**
     * @description handle page refresh
     */
    static effectInitialized = createAction(LineSwitcherActions.EFFECT_INITIALIZED);

    /**
     * @description Initialize line switcher (support only sport picks)
     */
    static initLineSwitcher = createAction(LineSwitcherActions.INIT_LINE_SWITCHER, props<{ pick: BetslipPick }>());

    /**
     * @description Show line switcher
     */
    static openSportsLineSwitcher = createAction(
        LineSwitcherActions.OPEN_LINE_SWITCHER,
        props<{ pick: BetslipV1Pick | BetslipV2OptionMarketPick }>(),
    );

    /**
     * @description Initialize option group for the selected pick
     */
    static updateOptionGroup = createAction(LineSwitcherActions.UPDATE_OPTION_GROUP, pprops<EventOptionGroup>());

    /**
     * @description Handle push updates
     */
    static handlePushUpdate = createAction(LineSwitcherActions.HANDLE_PUSH_UPDATE);

    /**
     * @description Update possible winnings on push update
     * @payload Possible winnings value
     */
    static updatePossibleWinningsState = createAction(
        LineSwitcherActions.UPDATE_POSSIBLE_WINNINGS_STATE,
        pprops<ILineSwitcherPossibleWinningsState>(),
    );

    /**
     * @description Update swap button state
     * @param isEnabled
     * @param buttonText - (optional)
     */
    static updateSwapButtonState = createAction(LineSwitcherActions.UPDATE_SWAP_BUTTON_STATE, pprops<ILineSwitcherSwapButtonState>());

    /**
     * @description Update error state
     * @param message - client message
     * @param logMessage - logging message
     */
    static updateErrorState = createAction(LineSwitcherActions.HANDLE_SWAP_ERROR, pprops<ILineSwitcherErrorState>());

    /**
     * @description Update new pick ID (selected inside the line switcher)
     */
    static addToSwapSelection = createAction(LineSwitcherActions.ADD_SWAP_PICK_SELECTION, props<{ pickId: V1PickId | V2OptionMarketPickId }>());

    /**
     * @description Replace a pick with the selected one
     */
    static swapSportPick = createAction(LineSwitcherActions.SWAP_PICK, props<{ model: EventModel }>());

    /**
     * @description Remove highlight from the newly switched pick
     */
    static removePickHighlight = createAction(LineSwitcherActions.REMOVE_PICK_HIGHLIGHT);

    /**
     * @description Close line switcher dialog and clear the state
     */
    static closeLineSwitcher = createAction(LineSwitcherActions.CLOSE_LINE_SWITCHER);

    /**
     * @description Cancel line switcher dialog and clear the state
     */
    static cancelLineSwitcher = createAction(LineSwitcherActions.CANCEL_LINE_SWITCHER);

    /**
     * @description Init onboarding message box
     */
    static initHelpMessageBox = createAction(LineSwitcherActions.INIT_HELP_MESSAGE_BOX);

    /**
     * @description Close onboarding message box
     */
    static closeHelpMessageBox = createAction(LineSwitcherActions.CLOSE_HELP_MESSAGE_BOX);

    /**
     * @description TrackSwap
     */
    static trackSwap = createAction(LineSwitcherActions.TRACK_SWAP, props<{ oldPick: BetslipPick; newPick: BetslipPick }>());
}
